import dayjs from 'dayjs';
import { EntityMixin } from '../../../components/core/entity-mixin.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { Price } from '../price.js';
import { TimeZone } from '../../../time/timezone.js';
import { IEventOpenAccessProtocol, OpenAccessProtocol, OpenAccessProtocolType } from './open-access-protocol.js';
import { EventOpenAccessProtocol } from './event-open-access-protocol.js';
import { VersionRange } from '../../../models/core/version-range.js';
import { cloneData, isSameData, isSameList } from '../../dto/data-model-utils.js';
import { ComponentList } from '../../../components/core/component-list.js';
import { OpenAccessPassVariation } from './open-access-pass-variation.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { OpenAccessPass } from './open-access-pass.js';
import { PassEventValidityRule } from './pass-event-validity-rule.js';

export { PassEventValidityRule } from './pass-event-validity-rule.js';

export enum OpenAccessPassTemplateStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED',
}

// eslint-disable-next-line new-cap
export class OpenAccessPassTemplate extends EntityMixin(DataModel) {
  static readonly TYPE_ID: string = '18346d4a68be4a788bd4deecbf50b717';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly status: OpenAccessPassTemplateStatus,
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly creationDate: dayjs.Dayjs,
    public readonly price: Price,
    public readonly protocol: OpenAccessProtocol,
    public readonly components: ComponentList,
    public readonly variations: OpenAccessPassVariation[],
  ) {
    super();
  }

  get $typeId(): string {
    return OpenAccessPassTemplate.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(2, OpenAccessPassTemplate.SCHEMA_VERSION);
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): OpenAccessPassTemplate {
    return new OpenAccessPassTemplate(
        toEnumFromString(MapUtils.get<string>(map, '$status'), OpenAccessPassTemplateStatus),
        MapUtils.get<string>(map, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        Price.decode(MapUtils.get<string>(map, 'price')),
        context.fromData(MapUtils.get<SerializedData>(map, 'protocol')),
        ComponentList.deserialize(context, MapUtils.get<SerializedData[]>(map, 'components')),
        MapUtils.getList<OpenAccessPassVariation>(map, 'variations', (e) => {
          return context.fromData(e as SerializedData);
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      '$status': fromEnumAsString(this.status),
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'price': this.price.encode(),
      'protocol': context.toData(this.protocol),
      'protocolType': fromEnumAsString(this.protocol.type),
      'components': this.components.serialize(context),
      'variations': this.variations.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return new OpenAccessPassTemplate(
        this.status,
        this.uniqueId,
        this.businessRef.clone(),
        TimeZone.cloneDate(this.creationDate),
        this.price.clone(),
        cloneData(this.protocol),
        this.components.clone(),
        this.variations.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.status === this.status &&
        other.uniqueId === this.uniqueId &&
        other.creationDate === this.creationDate &&
        isSameList(other.variations, this.variations, isSameData) &&
        isSameData(other.businessRef, this.businessRef) &&
        isSameData(other.price, this.price) &&
        isSameData(other.protocol, this.protocol) &&
        isSameData(other.components, this.components);
  }

  copyWith(data: Partial<OpenAccessPassTemplate>): this {
    return new OpenAccessPassTemplate(
        data.status ?? this.status,
        data.uniqueId ?? this.uniqueId,
        data.businessRef ?? this.businessRef.clone(),
        data.creationDate ?? TimeZone.cloneDate(this.creationDate),
        data.price ?? this.price.clone(),
        data.protocol ?? cloneData(this.protocol),
        data.components ?? this.components.clone(),
        data.variations ?? this.variations.map((e) => e.clone()),
    ) as this;
  }

  findVariationById(variationId: string): OpenAccessPassVariation | null {
    for(let i = 0; i < this.variations.length; i++) {
      if(this.variations[i].uniqueId == variationId) {
        return this.variations[i];
      }
    }

    return null;
  }

  generatePassCode(): string {
    switch(this.protocol.type) {
      case OpenAccessProtocolType.PerEvent:
      case OpenAccessProtocolType.PerSeat:
      case OpenAccessProtocolType.StagedPerSeat:
      case OpenAccessProtocolType.LimitedTime:
      case OpenAccessProtocolType.LimitedUses:
      case OpenAccessProtocolType.GymAccess:
        return OpenAccessPass.generateQrCode();
      case OpenAccessProtocolType.FlexiblePass:
        return OpenAccessPass.generateVoucherCode();
    }
  }

  static encodedStatusSafe(status: OpenAccessPassTemplateStatus): string {
    return fromEnumAsString(status);
  }
}

export class OpenAccessPassTemplateBuilder extends DataModelBuilder<OpenAccessPassTemplate> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassTemplate {
    return OpenAccessPassTemplate.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OpenAccessPassTemplate.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OpenAccessPassTemplate.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [protocol] field of type [OpenAccessProtocol] was introduced
    if(version < 2) {
      const events = IEventOpenAccessProtocol.convertFromLegacyEventList(
          MapUtils.getList<string>(map, 'events', (item) => JSONUtils.decodeString(item))
      );

      map['protocol'] = context.toData(EventOpenAccessProtocol.forTemplate({
        eventValidityRule: PassEventValidityRule.IncludedEvents,
        eventConfiguration: events,
      }));

      version = 2;
    }

    // In version 3 we added `variations` of type `OpenAccessPassVariation[]`.
    if(version < 3) version = 3;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
